/**
 * Upcoming-session card. The club Schedule list and the home page both render
 * this so a session looks the same wherever it appears. Admin-only controls
 * (Remind, Courts, Results) are gated by `isAdmin`; the home page passes false.
 */
import { useState } from "react";

import { remindSession, type SessionDTO } from "../api";

export function SessionRow({
  session,
  isAdmin,
  onRsvp,
  onShowRsvps,
  onOpenChat,
  onAssignCourts,
  onRecord,
  onShowResults,
  resultsVisible,
}: {
  session: SessionDTO;
  isAdmin: boolean;
  onRsvp: (sessionId: string, intent: "yes" | "no") => void;
  onShowRsvps: () => void;
  onOpenChat: () => void;
  onAssignCourts: () => void;
  onRecord: () => void;
  onShowResults: () => void;
  resultsVisible: boolean;
}) {
  // Pull "Tue Jun 3" out of the server-formatted display ("Tue Jun 3, 6:00…")
  const m = session.starts_at_display.match(/^(\w+)\s+(\w+)\s+(\d+)/);
  const weekday = (m?.[1] ?? "").toLowerCase();
  const month = (m?.[2] ?? "").toUpperCase();
  const day = m?.[3] ?? "";
  const detail = session.starts_at_display.replace(/^[^,]+,\s*/, "");

  return (
    <div className="border border-ink/15 rounded-md px-4 sm:px-5 py-4 bg-bg hover:border-ink/30 transition">
      {/* Top row: date + title — same shape on all sizes. */}
      <div className="flex items-start gap-4">
        <div className="text-center shrink-0 w-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55">
            {month || weekday}
          </div>
          <div className="font-display font-bold text-2xl leading-none num-tab">{day}</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-base leading-tight">{detail}</div>
          <div className="text-sm text-ink/55 truncate">{session.location ?? "TBD"}</div>
          {session.format && (
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55 mt-0.5">
              {session.format === "singles" ? "Singles" : "Doubles"}
              {session.court_count != null
                ? ` · ${session.court_count} court${session.court_count === 1 ? "" : "s"}`
                : ""}
            </div>
          )}
        </div>
        {/* Count on the right at >=sm; moves to its own row below sm. */}
        <div className={
          "hidden sm:block font-display font-bold text-xl px-3 py-1 num-tab rounded-sm whitespace-nowrap " +
          (session.confirmed > 0 ? "bg-ink text-accent" : "text-ink/40")
        }>
          {session.confirmed}{session.capacity != null ? `/${session.capacity}` : ""}
        </div>
      </div>

      {/* Action row: count (mobile-only), your-status, "see who" link, and
          quick RSVP buttons. Stacks below the title on mobile. */}
      <div className="mt-3 pt-3 border-t border-ink/10 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className={
            "sm:hidden font-display font-bold text-base px-2.5 py-0.5 num-tab rounded-sm " +
            (session.confirmed > 0 ? "bg-ink text-accent" : "text-ink/40")
          }>
            {session.confirmed}{session.capacity != null ? `/${session.capacity}` : ""}
          </div>
          {/* RSVP status + control: Playing? Yes | No. The highlighted half is
              your current answer; neither filled means you haven't replied. */}
          <div className="inline-flex items-center gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink/55">
              Playing?
            </span>
            <div className="inline-flex border border-ink/20 rounded-sm overflow-hidden">
              <button
                onClick={() => onRsvp(session.id, "yes")}
                className={
                  "cs-tap px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold transition " +
                  (session.my_status === "confirmed" || session.my_status === "waitlist"
                    ? "bg-accent text-ink"
                    : "text-ink/70 hover:bg-ink/5")
                }
              >
                Yes
              </button>
              <button
                onClick={() => onRsvp(session.id, "no")}
                className={
                  "cs-tap px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold border-l border-ink/20 transition " +
                  (session.my_status === "no"
                    ? "bg-ink text-bg"
                    : "text-ink/70 hover:bg-ink/5")
                }
              >
                No
              </button>
            </div>
            {session.my_status === "waitlist" && (
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink/55">
                · waitlist
              </span>
            )}
          </div>
          {session.my_court != null && (
            <div className="font-display font-bold text-[10px] uppercase tracking-wider bg-accent text-ink px-2 py-0.5 rounded-sm">
              🎾 Court {session.my_court_label ?? session.my_court}
            </div>
          )}
          <button
            onClick={onShowRsvps}
            className="font-mono text-[10px] uppercase tracking-wider text-ink/55 hover:text-ink underline decoration-accent-dim decoration-2 underline-offset-2"
          >
            Who & activity →
          </button>
        </div>
        <div className="flex flex-wrap gap-1 items-center justify-end">
          <button
            onClick={onOpenChat}
            className="cs-tap px-3 py-1.5 border border-ink/20 hover:border-ink/40 text-[10px] uppercase tracking-wider font-bold"
          >
            💬 Chat
          </button>
          {isAdmin && <RemindButton sessionId={session.id} />}
          {isAdmin && session.format != null && session.court_count != null && (
            <button
              onClick={onAssignCourts}
              className="cs-tap px-3 py-1.5 border border-ink/20 hover:border-ink/40 text-[10px] uppercase tracking-wider font-bold"
            >
              Courts
            </button>
          )}
          {/* Players who attended (or admins) can post the result. */}
          {(isAdmin || session.my_status === "confirmed") && session.format != null && (
            <button
              onClick={onRecord}
              className="cs-tap px-3 py-1.5 border border-ink/20 hover:border-ink/40 text-[10px] uppercase tracking-wider font-bold"
            >
              Record
            </button>
          )}
          {(isAdmin || resultsVisible) && (
            <button
              onClick={onShowResults}
              className="cs-tap px-3 py-1.5 border border-ink/20 hover:border-ink/40 text-[10px] uppercase tracking-wider font-bold"
            >
              Results
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Per-game admin nudge: emails THIS session's Yes/No prompt to everyone who
// hasn't RSVP'd yet. Deterministic (no LLM); shows the count it reached.
function RemindButton({ sessionId }: { sessionId: string }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  async function go() {
    setBusy(true);
    setMsg(null);
    try {
      const r = await remindSession(sessionId);
      const n = r.reminded ?? 0;
      setMsg(n === 0 ? "All RSVP'd" : `✓ Reminded ${n}`);
    } catch (e) {
      setMsg(String((e as Error).message).slice(0, 40));
    } finally {
      setBusy(false);
    }
  }
  if (msg) {
    return (
      <span className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-success">
        {msg}
      </span>
    );
  }
  return (
    <button
      onClick={() => void go()}
      disabled={busy}
      title="Email this game's RSVP prompt to everyone who hasn't answered"
      className="cs-tap px-3 py-1.5 border border-ink/20 hover:border-ink/40 text-[10px] uppercase tracking-wider font-bold disabled:opacity-40"
    >
      {busy ? "Sending…" : "🔔 Remind"}
    </button>
  );
}
